/**
 * Centralized API Client with request/response logging.
 *
 * All API calls should go through this module so that:
 * 1. Every request logs: method, full URL, and whether auth is attached
 * 2. Every response logs: status, statusText, and elapsed time
 * 3. Errors log: status, response body, and elapsed time
 *
 * This makes it easy to diagnose routing, auth, and connectivity issues
 * in both development (browser console) and production (Cloud Run → nginx).
 */

import { useAuthStore } from '../auth/authStore';

// Canonical base — matches the backend's /api/v1 router prefix.
// In production the React build sets REACT_APP_API_URL via cloudbuild.yaml;
// in dev it falls back to the relative path which the setupProxy handles.
export const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const LOG_PREFIX = '[API]';

/**
 * Get auth headers (Content-Type + Bearer token if logged in).
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  try {
    const token = await useAuthStore.getState().refreshToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch (err) {
    console.warn(`${LOG_PREFIX} Failed to get auth token:`, err);
  }
  return headers;
}

/**
 * Logged fetch wrapper.
 *
 * Drop-in replacement for `fetch()` — same signature, same return type,
 * but logs request and response details to the console.
 */
export async function fetchApi(
  url: string,
  init?: RequestInit,
): Promise<Response> {
  const method = init?.method?.toUpperCase() || 'GET';
  const hasAuth = !!(init?.headers as Record<string, string>)?.['Authorization'];

  console.log(
    `${LOG_PREFIX} → ${method} ${url}` + (hasAuth ? ' [auth]' : ' [no-auth]'),
  );

  const start = performance.now();
  let response: Response;

  try {
    response = await fetch(url, init);
  } catch (err) {
    const elapsed = Math.round(performance.now() - start);
    console.error(
      `${LOG_PREFIX} ✕ ${method} ${url} — network error after ${elapsed}ms:`,
      err,
    );
    throw err;
  }

  const elapsed = Math.round(performance.now() - start);

  if (response.ok) {
    console.log(
      `${LOG_PREFIX} ← ${response.status} ${response.statusText} — ${method} ${url} (${elapsed}ms)`,
    );
  } else {
    // Clone so the caller can still read the body
    const body = await response.clone().text().catch(() => '<unreadable>');
    console.error(
      `${LOG_PREFIX} ✕ ${response.status} ${response.statusText} — ${method} ${url} (${elapsed}ms)`,
      body.substring(0, 500),
    );
  }

  return response;
}
